var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { CloseCircle, InputShow, InputHide } from '@volkswagen-onehub/icons-core';
import { styled, css } from '../theme-provider';
import { CharacterCounter } from '../character-counter';
import { getAuxillaryColor, getAuxillaryTextColor, getMainColor, getLabelColor, } from '../helpers/input';
import { Spinner } from '../spinner';
import { getCommonProps } from '../helpers/common-props';
import { createA11yOutlineStyles } from '../helpers/a11y';
import { InputNotification } from '../helpers/input-notification';
const iconTransitionDuration = '0.15s';
const clearClickAreaSize = 44;
const clearIconSize = 24;
const clearIconOffset = (clearClickAreaSize - clearIconSize) / 2;
const inputClearPadding = `${clearClickAreaSize - clearIconOffset}px`;
const StyledInput = styled.input.withConfig({ displayName: "StyledInput", componentId: "sc-dkk40" }) `width:100%;padding:${({ theme }) => parseInt(theme.size.static100) / 2}px 0;padding-inline-start:${({ icon, hasValue, placeholder, theme }) => icon && !hasValue && placeholder ? theme.size.static400 : '0'};padding-inline-end:${({ hasClearButton, appearance }) => (hasClearButton || appearance === 'error' || appearance === 'success')
    ? inputClearPadding
    : '0'};border:0;border-radius:0;background:transparent;cursor:${({ appearance }) => (appearance === 'disabled' ? 'not-allowed' : '')};color:${getMainColor};outline:none;-webkit-appearance:none;font-size:${({ theme }) => theme.fonts.copy[200].fontSize};font-family:inherit;text-overflow:ellipsis;transition:${(props) => 
/*
    "padding" instead of "padding-inline-start" because Safari (15.6.1)
    will not animate the padding otherwise
*/
props.hasValue ? 'none' : `padding ${iconTransitionDuration} ease-in-out`};${({ isFloatingLabel }) => (isFloatingLabel) &&
    css `
			&:not(:focus-within)::placeholder {
				opacity: 0;
			}
		`} &:placeholder-shown{text-overflow:ellipsis;}&::placeholder{opacity:1;color:${({ theme }) => theme.colors.content.secondary};}&::-webkit-input-placeholder{color:${({ theme }) => theme.colors.content.secondary};}&:focus:not(:read-only){padding-inline-start:0;}&::-ms-reveal{display:none;}&::-ms-clear{display:none;width:0;height:0;}&::-webkit-search-decoration,&::-webkit-search-cancel-button,&::-webkit-search-results-button,&::-webkit-search-results-decoration{display:none;}`;
const StyledLabel = styled.label.withConfig({ displayName: "StyledLabel", componentId: "sc-1qwsrsp" }) `display:flex;flex-direction:column;justify-content:center;gap:2px;width:100%;height:50px;padding:0 ${({ theme }) => theme.size.static250};position:relative;color:${getLabelColor};background-color:${({ appearance, theme }) => appearance === 'readonly' && theme.colors.background.tertiary};box-shadow:0 0 0 ${({ theme }) => theme.size.border.light} ${getAuxillaryColor};border-radius:${({ theme }) => theme.size.static150};${({ appearance }) => (appearance === 'success' ||
    appearance === 'error' ||
    appearance === 'information') &&
    css `
			box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
				${getAuxillaryColor};
		`} ${({ appearance }) => (appearance === 'default' || appearance === 'validation') &&
    css `
			&:hover {
				transition: box-shadow 300ms ease;
				-webkit-transition: box-shadow 300ms ease;
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.light}
					${({ theme }) => theme.colors.border.hover};
			}
			&:focus-within {
				box-shadow: 0 0 0 ${({ theme }) => theme.size.border.heavy}
					${({ theme }) => theme.colors.focus.main};
			}
		`} ${({ hasIcon, theme, isFloatingLabel }) => !isFloatingLabel &&
    css `
			${StyledSmallLabelText} {
				padding-inline-start: ${hasIcon ? theme.size.static400 : '0'};
				transition: padding-inline-start ${iconTransitionDuration} ease-in-out;
			}
			&:focus-within ${StyledSmallLabelText} {
				padding-inline-start: 0;
				transition: padding-inline-start ${iconTransitionDuration} ease-in-out
					${iconTransitionDuration};
			}
		`} ${({ appearance, isFloatingLabel, theme }) => appearance !== 'readonly' &&
    isFloatingLabel &&
    css `
			/* On focus / while typing: Label-style, above the input */
			&:focus-within ${StyledSmallLabelText} {
				max-height: ${theme.size.static250};
				transition: max-height ${iconTransitionDuration} ease-in-out
					${iconTransitionDuration};
			}

			${StyledSmallLabelText} {
				transition: max-height ${iconTransitionDuration} ease-in-out;
			}
		`} ${({ hasMessage, appearance, theme }) => hasMessage &&
    appearance === 'information' &&
    css `
			margin-bottom: ${theme.size.static400};
		`} ${({ hasMessage, appearance, theme }) => hasMessage &&
    (appearance === 'success' || appearance === 'error') &&
    css `
			&:focus-within {
				margin-bottom: ${theme.size.static400};
			}
		`} ${({ hasValue, theme }) => hasValue &&
    css `
			${StyledSmallLabelText} {
				max-height: ${theme.size.static250};
			}
		`}`;
/**
 * Basic version of the label.
 * If there is a floating label, this one only acts as an invisible spacer.
 * We need this spacer because we cannot know its height if the user has
 * text-only zoom activated and/or there are line breaks.
 */
const StyledSmallLabelText = styled.p.withConfig({ displayName: "StyledSmallLabelText", componentId: "sc-14oup5p" }) `font-size:${({ theme }) => theme.fonts.label[100].fontSize};margin:0;padding:0;visibility:${({ isSpacer }) => (isSpacer ? 'hidden' : 'initial')};max-height:${({ isSpacer, theme }) => (isSpacer ? '0' : theme.size.static250)};transition:max-height 0.25s ease-in-out;`;
const StyledFloatingLabelText = styled.p.withConfig({ displayName: "StyledFloatingLabelText", componentId: "sc-3d3za9" }) `position:absolute;margin:0;transition:all 0.25s ease-in-out;${({ hasValue, hasIcon, theme, hasClearButton }) => {
    // Label-style, above the input
    if (hasValue) {
        return css `
				bottom: 100%;
				cursor: initial;
				font-size: ${theme.fonts.label[100].fontSize};
				padding: 0;
			`;
    }
    // Placeholder style, "inside" the input
    return css `
			bottom: 0;
			cursor: text;
			font-size: ${theme.fonts.copy[200].fontSize};
			padding: ${theme.size.static100} 0;
			padding-inline-start: ${hasIcon ? theme.size.static400 : '0'};
			padding-inline-end: ${hasClearButton ? inputClearPadding : '0'};
		`;
}} ${StyledInput}:focus:not(:read-only)~ &{bottom:100%;padding:0;font-size:${({ theme }) => theme.fonts.label[100].fontSize};cursor:initial;background:transparent;color:${getMainColor};}`;
const StyledIcon = styled.div.withConfig({ displayName: "StyledIcon", componentId: "sc-1ay2o8c" }) `display:flex;position:absolute;opacity:${({ hasValue }) => (hasValue ? 0 : 1)};transition:${({ hasValue }) => hasValue
    ? 'none'
    : `all ${iconTransitionDuration} ease-in-out
		${iconTransitionDuration}`};${StyledLabel}:focus-within &{opacity:0;transition:all ${iconTransitionDuration} ease-in-out;}`;
const StyledAdditionalInfo = styled.div.withConfig({ displayName: "StyledAdditionalInfo", componentId: "sc-141e8wv" }) `display:flex;flex-direction:row;justify-content:space-between;align-items:flex-start;gap:${({ theme }) => theme.size.static150};width:100%;color:${getAuxillaryTextColor};position:absolute;bottom:0;left:0;padding-top:2px;transform:translateY(100%);`;
const StyledClearButton = styled.button.withConfig({ displayName: "StyledClearButton", componentId: "sc-k6jebn" }) `position:relative;width:${clearClickAreaSize}px;height:${clearClickAreaSize}px;transform:translateX(\n\t\t${({ theme }) => theme.direction === 'rtl'
    ? `-${clearIconOffset}px`
    : `${clearIconOffset}px`}\n\t);background:transparent;padding:0;border:none;line-height:0;cursor:${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};outline:none;-webkit-appearance:none;pointer-events:initial;color:${({ disabled, theme }) => theme.colors.button.primary[disabled ? 'disabled' : 'default']};:hover:not(:disabled){color:${({ theme }) => theme.colors.button.primary.hover};}:focus-visible{${({ theme }) => createA11yOutlineStyles(theme)}}`;
const StyledClearButtonWrapper = styled.div.withConfig({ displayName: "StyledClearButtonWrapper", componentId: "sc-3jqzog" }) `position:absolute;right:0;left:0;top:calc(50% - ${({ theme }) => theme.size.static150});transform:translateY(-50%);pointer-events:none;display:flex;flex-direction:row-reverse;`;
const StyledInputLineWrapper = styled.div.withConfig({ displayName: "StyledInputLineWrapper", componentId: "sc-19kmswn" }) `position:relative;`;
const SpinnerWrapper = styled.div.withConfig({ displayName: "SpinnerWrapper", componentId: "sc-10k8t3q" }) `position:absolute;${({ theme }) => (theme.direction === 'rtl' ? `left: 0;` : `right: 0;`)} top:0;`;
export const TextInputInner = (props) => {
    const { additionalFeedback, appearance = 'default', autoFocus, clearIconLabel = '', customId, defaultValue, hasClearButton, icon, innerRef, isClearButtonVisible, label = '', maxLength, name, onBlur, onChange, onKeyDown, onClear, onFocus, isFloating: isFloatingFromProps, message = '', placeholder, required = false, togglePasswordVisibilityIconLabel = '', type, value, autoComplete, inputMode, characterCountLimit, max, min, step } = props, rest = __rest(props, ["additionalFeedback", "appearance", "autoFocus", "clearIconLabel", "customId", "defaultValue", "hasClearButton", "icon", "innerRef", "isClearButtonVisible", "label", "maxLength", "name", "onBlur", "onChange", "onKeyDown", "onClear", "onFocus", "isFloating", "message", "placeholder", "required", "togglePasswordVisibilityIconLabel", "type", "value", "autoComplete", "inputMode", "characterCountLimit", "max", "min", "step"]);
    const isFloating = placeholder ? false : isFloatingFromProps;
    const [hasValue, setHasValue] = React.useState(Boolean(value || defaultValue));
    const [currentInputType, setCurrentInputType] = React.useState(type);
    const [characterCount, setCharacterCount] = React.useState((value === null || value === void 0 ? void 0 : value.length) || (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.length) || 0);
    const [isFocused, setIsFocused] = React.useState(false);
    const commonProps = getCommonProps(props);
    const isPasswordInput = type === 'password';
    const labelText = (React.createElement(React.Fragment, null,
        label,
        required && '*'));
    const isDisabled = appearance === 'disabled';
    const isReadOnly = appearance === 'readonly';
    const validateInputField = (event) => {
        setHasValue(Boolean(event.currentTarget.value));
    };
    const handleBlur = (event) => {
        validateInputField(event);
        setIsFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    };
    const handleChange = (event) => {
        var _a, _b;
        // needed for the label to float when the input is filled in through browser's autocomplete
        setHasValue(Boolean(event.target.value));
        if (typeof value === 'undefined') {
            // not a controlled component, update character counter
            setCharacterCount((_b = (_a = event.target.value) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0);
        }
        if (onChange) {
            onChange(event);
        }
    };
    const handleFocus = (event) => {
        setIsFocused(true);
        if (onFocus) {
            onFocus(event);
        }
    };
    const isNotificationAppearance = (appearance) => ['error', 'success', 'information'].includes(appearance);
    /*
    ariaInvalid and ariaDescribedBy are used to set the aria-invalid and aria-describedby attributes
    according to the appearance of the input. These properties are needed to make the input accessible.
    */
    const ariaInvalid = appearance === 'error';
    const ariaDescribedBy = isNotificationAppearance(appearance)
        ? `${appearance}-message-${customId}`
        : undefined;
    React.useEffect(() => {
        var _a;
        setCharacterCount((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0);
    }, [value]);
    return (React.createElement(StyledLabel, Object.assign({ appearance: appearance, isFloatingLabel: isFloating, hasValue: hasValue, hasMessage: message, hasIcon: Boolean(icon) }, commonProps, rest),
        icon && React.createElement(StyledIcon, { hasValue: hasValue }, icon),
        React.createElement(StyledSmallLabelText, { isSpacer: isFloating }, labelText),
        React.createElement(StyledInputLineWrapper, null,
            React.createElement(StyledInput, { appearance: appearance, autoFocus: autoFocus, defaultValue: defaultValue, "aria-required": required, required: required, disabled: isDisabled, icon: icon, maxLength: maxLength, name: name, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus, onKeyDown: onKeyDown, isFloatingLabel: isFloating, placeholder: placeholder, readOnly: isReadOnly, "aria-readonly": isReadOnly, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, ref: innerRef, type: currentInputType, value: value, autoComplete: autoComplete, hasValue: hasValue, hasClearButton: hasClearButton, inputMode: inputMode, max: max, min: min, step: step }),
            isPasswordInput && currentInputType === 'password' && (React.createElement(StyledClearButtonWrapper, null,
                React.createElement(StyledClearButton, { "aria-label": togglePasswordVisibilityIconLabel, title: togglePasswordVisibilityIconLabel, onClick: () => setCurrentInputType('text'), type: "button", disabled: appearance === 'disabled' },
                    React.createElement(InputShow, { ariaHidden: true })))),
            isPasswordInput && currentInputType === 'text' && (React.createElement(StyledClearButtonWrapper, null,
                React.createElement(StyledClearButton, { "aria-label": togglePasswordVisibilityIconLabel, title: togglePasswordVisibilityIconLabel, onClick: () => setCurrentInputType('password'), type: "button", disabled: appearance === 'disabled' },
                    React.createElement(InputHide, null)))),
            !isPasswordInput && hasClearButton && isClearButtonVisible && (React.createElement(StyledClearButtonWrapper, null,
                React.createElement(StyledClearButton, { "aria-label": clearIconLabel, title: clearIconLabel, onClick: onClear, type: "button", disabled: appearance === 'disabled' },
                    React.createElement(CloseCircle, { variant: "default", ariaHidden: true })))),
            isFloating && (React.createElement(StyledFloatingLabelText, { hasIcon: Boolean(icon), hasValue: hasValue, hasClearButton: hasClearButton }, labelText)),
            appearance === 'validation' && (React.createElement(SpinnerWrapper, null,
                React.createElement(Spinner, null)))),
        React.createElement(StyledAdditionalInfo, null,
            isNotificationAppearance(appearance) && (React.createElement(InputNotification, { appearance: appearance, id: ariaDescribedBy, text: message, isFocused: isFocused, hideIcon: appearance === 'information', iconVariant: "default" })),
            (characterCountLimit !== null && characterCountLimit !== void 0 ? characterCountLimit : (maxLength && maxLength > 0)) ? (React.createElement(CharacterCounter, { value: characterCount, limit: characterCountLimit || maxLength || 0 })) : null),
        additionalFeedback));
};
export const TextInput = React.forwardRef((props, ref) => React.createElement(TextInputInner, Object.assign({}, props, { innerRef: ref })));
